import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";

// "Chi siamo" top: breadcrumb + title on the page background, then the three photos (about-carousel-4.jpg was removed at the owner's request).
// Every photo is shown whole (native files, never cropped) at one shared height. On PC the photos share the full width of the page,
// each as wide as its proportions need, so the strip is always full; on mobile they stay a swipeable scroll-snap carousel.
const PHOTOS = [
  { src: "/founders/about-carousel-1.jpg", alt: "Alessia e Alessandra — FArLight, in movimento", w: 1335, h: 1600 },
  { src: "/founders/about-carousel-2.jpg", alt: "Alessia e Alessandra — FArLight, sedute schiena contro schiena", w: 925, h: 1152 },
  { src: "/founders/about-carousel-3.jpg", alt: "Il nostro studio, in movimento", w: 1234, h: 1600 },
];

export const AboutCarousel = ({ label }) => {
  const track = useRef(null);
  const [paused, setPaused] = useState(false);

  const step = (dir) => {
    const el = track.current;
    if (!el) return;
    const slides = Array.from(el.children);
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
    if (dir > 0 && atEnd) return el.scrollTo({ left: 0, behavior: "smooth" });
    if (dir < 0 && el.scrollLeft <= 4) return el.scrollTo({ left: el.scrollWidth, behavior: "smooth" });
    // a slide's snap position is its left edge minus the track's left padding
    const pad = parseFloat(getComputedStyle(el).paddingLeft) || 0;
    const pos = (s) => s.offsetLeft - pad;
    const target = dir > 0 ? slides.find((s) => pos(s) > el.scrollLeft + 8) : [...slides].reverse().find((s) => pos(s) < el.scrollLeft - 8);
    if (target) el.scrollTo({ left: pos(target), behavior: "smooth" });
  };

  useEffect(() => {
    if (paused || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const id = setInterval(() => step(1), 5000);
    return () => clearInterval(id);
  }, [paused]);

  return (
    <section data-testid="about-banner" className="pt-24 pb-12 lg:pb-16 bg-cream" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocus={() => setPaused(true)} onBlur={() => setPaused(false)}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-inksoft">
          <Link to="/" className="hover:text-ink">Home</Link> · {label}
        </p>
        <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink">{label}</h1>
      </div>
      <div className="relative mt-8">
        <div ref={track} data-testid="about-carousel" className="flex gap-3 sm:gap-4 overflow-x-auto snap-x snap-mandatory px-6 scroll-px-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:overflow-visible lg:max-w-7xl lg:mx-auto lg:px-12">
          {PHOTOS.map((p, i) => (
            // PC: flex-grow = width/height, so all photos get the same height and together fill the row exactly
            <div key={p.src} style={{ "--ar": p.w / p.h }} className="snap-start shrink-0 lg:shrink lg:min-w-0 lg:[flex:var(--ar)_1_0%]">
              <img data-testid={`about-banner-photo-${i}`} src={p.src} alt={p.alt} width={p.w} height={p.h} loading={i === 0 ? "eager" : "lazy"} draggable={false}
                className="block w-auto max-w-none h-[min(56vh,calc((100vw-3rem)*1.19))] max-h-[600px] rounded-sm lg:w-full lg:max-w-full lg:h-auto lg:min-h-0 lg:max-h-none" />
            </div>
          ))}
        </div>
        {/* arrows only where the photos scroll (mobile/tablet): on PC all three are visible */}
        <div className="max-w-7xl mx-auto px-6 lg:px-12 mt-5 flex justify-end gap-2 lg:hidden">
          <button type="button" data-testid="about-carousel-prev" aria-label="Foto precedente" onClick={() => step(-1)} className="h-10 w-10 grid place-items-center rounded-full border border-ink/25 text-ink hover:bg-ink hover:text-cream transition-colors">
            <ChevronLeft size={18} />
          </button>
          <button type="button" data-testid="about-carousel-next" aria-label="Foto successiva" onClick={() => step(1)} className="h-10 w-10 grid place-items-center rounded-full border border-ink/25 text-ink hover:bg-ink hover:text-cream transition-colors">
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
};
